import {ModalForm, ProFormCheckbox, ProFormGroup, ProFormList, ProFormSelect, ProFormText} from '@ant-design/pro-form';
import {Result} from 'antd';
import React from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate, useParams } from "react-router-dom"
import axiosApiInstance from "../../../../api/axiosClient"
import { ProSkeleton } from "@ant-design/pro-components"
import { API } from "../../../../common/constants"
import { useAtomValue } from "jotai"
import { queryClientAtom } from "jotai-tanstack-query"
import { spotifyArtistIdRules } from "../../../../common/spotifyArtistId"

const REDIRECT_URL = "/music/artists"

type FormContentProps = {
  initialValues: API.MusicDistribution.Artist
}

export type EditArtistFormProps = {
  artistKey?: string
  onClose?: () => void
}

const decodeArtistKey = (raw?: string) => {
  if (!raw) return ""
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

const EditArtistForm = ({ artistKey, onClose }: EditArtistFormProps) => {
  const navigate = useNavigate()
  const { key_name: paramKey } = useParams()
  const key_name = decodeArtistKey(artistKey ?? paramKey)
  const queryClient = useAtomValue(queryClientAtom)
  const isDarkMode = document.documentElement.classList.contains('dark-mode')

  const close = () => {
    if (onClose) {
      onClose()
      return
    }
    navigate(REDIRECT_URL)
  }

  const { data, status } = useQuery({
    queryKey: ["artist", key_name],
    enabled: Boolean(key_name),
    queryFn: () =>
      axiosApiInstance
        .get(`/api/products/artists/${encodeURIComponent(key_name)}`)
        .then(({ data }) => data),
  })

  const FormContent = ({ initialValues }: FormContentProps) => (
    <>
      <ProFormText
        label="Key Name"
        name="key_name"
        required
        rules={[
          {
            required: true,
          },
        ]}
        initialValue={initialValues.key_name}
        fieldProps={isDarkMode ? {
          style: { backgroundColor: '#262626', color: '#e0e0e0', borderColor: '#434343' }
        } : {}}
      />
      <ProFormText
        name="spotify_id"
        label="Spotify ID"
        initialValue={initialValues.spotify_id}
        tooltip="22-character Spotify artist ID, or paste an open.spotify.com / artists.spotify.com URL"
        rules={spotifyArtistIdRules}
        fieldProps={isDarkMode ? {
          style: { backgroundColor: '#262626', color: '#e0e0e0', borderColor: '#434343' }
        } : {}}
      />
      <ProFormText
        name="apple_music_id"
        label="Apple ID"
        initialValue={initialValues.apple_music_id}
        fieldProps={isDarkMode ? {
          style: { backgroundColor: '#262626', color: '#e0e0e0', borderColor: '#434343' }
        } : {}}
      />
      <ProFormList<API.MusicDistribution.Name>
        name="names"
        min={1}
        colProps={{ span: 24 }}
        initialValue={initialValues.names}
        style={isDarkMode ? { 
          backgroundColor: '#1f1f1f'
        } : {}}
        creatorButtonProps={{
          position: "bottom",
          creatorButtonText: "New Name",
          style: isDarkMode ? { backgroundColor: '#262626', borderColor: '#434343', color: '#e0e0e0' } : {}
        }}
      >
        {(f, index, action) => (
          <ProFormGroup
            align="center"
            direction="horizontal"
            rowProps={{ justify: "start" }}
            style={isDarkMode ? { 
              backgroundColor: '#262626',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '8px',
              border: '1px solid #434343'
            } : {
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '8px'
            }}
          >
            <ProFormText
              name="full_name"
              label="Full Name"
              placeholder={"Full Name"}
              width="md"
              rules={[
                {
                  required: true,
                },
              ]}
              required
              fieldProps={isDarkMode ? {
                style: { backgroundColor: '#262626', color: '#e0e0e0', borderColor: '#434343' }
              } : {}}
            />
            <ProFormSelect
              name="full_name_language"
              label="Language"
              mode="single"
              width="xs"
              options={[
                {
                  label: "🇬🇷",
                  value: "GR",
                },
                {
                  label: "🇬🇧",
                  value: "EN",
                },
              ]}
              fieldProps={isDarkMode ? {
                style: { backgroundColor: '#262626', color: '#e0e0e0', borderColor: '#434343' }
              } : {}}
            />
            <ProFormCheckbox
              key={"is_default"}
              name={"is_default"}
              label="Default?"
              fieldProps={isDarkMode ? {
                style: { color: '#e0e0e0' }
              } : {}}
            />
            <ProFormCheckbox
              key={"is_stage_name"}
              name={"is_stage_name"}
              label="Stage Name?"
              fieldProps={isDarkMode ? {
                style: { color: '#e0e0e0' }
              } : {}}
            />
          </ProFormGroup>
        )}
      </ProFormList>
    </>
  )

  const body =
    status === "success" && data ? (
      <FormContent initialValues={data} />
    ) : status === "error" ? (
      <Result title="Failed to load artist" status="error" />
    ) : (
      <ProSkeleton type="list" />
    )

  return (
    <ModalForm<API.MusicDistribution.Artist>
      title="Edit artist"
      labelCol={{ span: 3 }}
      wrapperCol={{ span: 21 }}
      width={1000}
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close()
      }}
      modalProps={{
        destroyOnClose: true,
        onCancel: close,
      }}
      onFinish={async (values) => {
        try {
          await axiosApiInstance.put(`/api/products/artists/`, values)
          queryClient.invalidateQueries({ queryKey: ["artists"] })
        } catch (reason) {
          console.log(reason)
        } finally {
          close()
        }
        return true
      }}
    >
      {body}
    </ModalForm>
  )
}

export default EditArtistForm;
